import catalogue from "./cqc-profile-catalogue.json";
import { elementGuidance } from "./cqcTaxonomy";
import {
  applicabilityIndex,
  itemApplicableForSite,
  assignmentIndex,
  attestationIndex,
  evidenceItemIndex,
  elementStatus,
} from "./cqcRollup";
import {
  PROFILE_VERSION,
  countryOf,
  profileReady,
  selectionFor,
  groupsForSite,
} from "./cqcFrameworks";
import frameworkEngine from "./care_frameworks";
export { catalogue, profileReady };
export const scopeKey = (groupId, itemId, elementId) =>
  `${groupId}|${itemId}|${elementId}`;
export const rowKey = (siteId, groupId, itemId, elementId) =>
  `${siteId}|${scopeKey(groupId, itemId, elementId)}`;
export function blankProfile(site) {
  const old = site?.serviceProfile || {},
    country = countryOf(site);
  const p = {
    serviceTypes: [],
    functions: Object.fromEntries(
      catalogue.functions.map((f) => [f.id, "unknown"]),
    ),
    stage: "operating",
    careSetting: "unknown",
    notes: "",
    decisions: {},
    ...old,
    jurisdiction: country,
    catalogueVersion: PROFILE_VERSION,
    frameworkCatalogueVersion: frameworkEngine.definitions.version,
    addressRevision: site?.addressRevision || 0,
  };
  p.frameworkId =
    country === "England"
      ? catalogue.frameworkId
      : selectionFor(p, country).frameworkId;
  return p;
}
export function reconcilePlan(profile) {
  const selected = selectionFor(profile, profile.jurisdiction),
    keys = frameworkEngine.keysForProfile(profile, profile.jurisdiction);
  return {
    ...profile,
    frameworkId: selected.frameworkId,
    decisions: Object.fromEntries(
      keys.map((k) => [
        k,
        profile.decisions?.[k] || suggestedDecision(profile, ...k.split("|")),
      ]),
    ),
  };
}

// Recommendations affect the internal collection plan only. No service/function
// automatically makes an entire quality statement legally inapplicable.
export function suggestedDecision(profile, groupId, itemId, elementId) {
  if (profile.jurisdiction !== "England")
    return {
      mode: "required",
      reason: "Included in the reviewed national/service evidence plan.",
    };
  let primary = ["peoples-experience", "staff-feedback", "processes"];
  if (
    [
      "governance",
      "partnerships",
      "team-working",
      "safe-systems",
      "continuity",
    ].includes(itemId)
  )
    primary.push("partner-feedback");
  if (
    [
      "monitoring-outcomes",
      "equity-outcomes",
      "learning-improvement",
      "environmental-sustainability",
    ].includes(itemId)
  )
    primary.push("outcomes");
  if (
    [
      "workforce-wellbeing",
      "workforce-equality",
      "environmental-sustainability",
      "freedom-speak-up",
    ].includes(itemId)
  )
    primary = ["staff-feedback", "processes", "outcomes"];
  const handsOn = profile.serviceTypes.some((t) =>
    [
      "dental",
      "care-home",
      "nursing-home",
      "supported-living",
      "hospital",
      "mental-health",
      "community-hospice",
      "ambulance",
      "diagnostics",
    ].includes(t),
  );
  if (
    handsOn &&
    [
      "safe-environments",
      "infection-control",
      "kindness-dignity",
      "immediate-needs",
    ].includes(itemId)
  )
    primary.push("observation");
  if (
    itemId === "medicines-optimisation" &&
    ["prescribing", "administration", "prompting"].some(
      (f) => profile.functions[f] === "yes",
    )
  )
    primary.push("observation", "outcomes");
  if (
    profile.functions.remote === "yes" &&
    ["providing-information", "equity-access", "consent"].includes(itemId)
  )
    primary.push("observation");
  if (
    itemId === "planning-future" &&
    profile.functions["end-of-life"] === "yes"
  )
    primary.push("partner-feedback", "outcomes");
  const required = primary.includes(elementId);
  return {
    mode: required ? "required" : "supporting",
    reason: required
      ? "Included in the organisation’s reviewed evidence plan."
      : "Supporting category in this service plan; retain relevant evidence when available. This is not an exemption from CQC assessment.",
  };
}
export function suggestedPlan(profile) {
  const decisions = {};
  for (const g of frameworkEngine.groupsForProfile(
    profile,
    profile.jurisdiction,
  ))
    for (const i of g.items)
      for (const e of i.elements)
        decisions[scopeKey(g.id, i.id, e.id)] = suggestedDecision(
          profile,
          g.id,
          i.id,
          e.id,
        );
  return decisions;
}
export function decisionFor(site, groupId, itemId, elementId, apps = {}) {
  if (!itemApplicableForSite(apps, site._id, groupId, itemId))
    return {
      mode: "not-applicable",
      reason:
        apps[`${site._id}|${groupId}|${itemId}`]?.reason ||
        "Existing statement exclusion",
    };
  if (!profileReady(site))
    return {
      mode: "profile-needed",
      reason: "Confirm the service profile before calculating readiness.",
    };
  return (
    site.serviceProfile.decisions?.[scopeKey(groupId, itemId, elementId)] || {
      mode: "required",
      reason: "No exclusion recorded.",
    }
  );
}
export function buildCqcRows({
  sites = [],
  assignments = [],
  attestations = [],
  applicability = [],
  evidenceItems = [],
} = {}) {
  const apps = applicabilityIndex(applicability),
    assign = assignmentIndex(assignments),
    sources = attestationIndex(attestations),
    evidence = evidenceItemIndex(evidenceItems);
  return sites.flatMap((site) =>
    groupsForSite(site).flatMap((group) =>
      group.items.flatMap((item) =>
        item.elements.map((element) => {
          const key = rowKey(site._id, group.id, item.id, element.id),
            scope = decisionFor(site, group.id, item.id, element.id, apps);
          const applicable = scope.mode !== "not-applicable";
          return {
            key,
            site,
            group,
            item,
            element,
            scope,
            applicable,
            assignment: assign[key],
            source: sources[key],
            evidenceItems: evidence[key] || [],
            status: elementStatus({ applicable, assignment: assign[key] }),
          };
        }),
      ),
    ),
  );
}
export function readiness(rows) {
  const required = rows.filter((r) => r.scope.mode === "required"),
    completed = required.filter((r) => r.status === "completed").length;
  return {
    total: required.length,
    completed,
    ongoing: required.filter((r) => r.status === "ongoing").length,
    notStarted: required.filter((r) => r.status === "not-started").length,
    configured: required.filter((r) => r.source).length,
    supporting: rows.filter((r) => r.scope.mode === "supporting").length,
    excluded: rows.filter((r) => r.scope.mode === "not-applicable").length,
    pending: rows.filter((r) => r.scope.mode === "profile-needed").length,
    pct: required.length ? (completed / required.length) * 100 : null,
  };
}

const SERVICE_CONTEXT = {
  dental:
    "Apply this to the dental treatments actually provided, patient consent, decontamination, equipment and referral arrangements.",
  "care-home":
    "Apply this to residents’ daily care, nutrition, falls, dignity, staffing and the safety of the home.",
  "nursing-home":
    "Include residents’ daily support and the nursing interventions actually delivered, with clinical competencies and escalation arrangements.",
  homecare:
    "Apply this to care delivered in people’s own homes, visit reliability, lone working, continuity and individual home risks.",
  "supported-living":
    "Focus on personal support, choice, independence and tenancy boundaries. Separate the care provider’s duties from those of the landlord.",
  "shared-lives":
    "Focus on approved carers, matching, supervision and the person’s experience and outcomes in the Shared Lives arrangement.",
  gp: "Apply this to appointment access, assessment, prescribing, referrals, results and ongoing clinical care provided by this practice.",
  clinic:
    "Use the actual clinic model: assessment, consent, treatment, follow-up, referral and escalation, including remote delivery where used.",
  hospital:
    "Collect evidence from each relevant department or core service; a group policy alone does not demonstrate local clinical practice.",
  "mental-health":
    "Focus on therapeutic care, consent and capacity, risk, recovery and any restrictive practice or detention within the service’s scope.",
  "community-hospice":
    "Use evidence of coordinated community or hospice care, symptom management, individual wishes and clinical escalation.",
  ambulance:
    "Use evidence of dispatch, vehicle and equipment readiness, staff competence, patient transfers and clinical handovers for the transport provided.",
  diagnostics:
    "Use evidence of patient/sample identification, equipment assurance, accuracy, reporting and escalation of urgent or abnormal findings.",
};
const TOPICS = {
  "learning-culture":
    "Show a concern or incident being identified, reviewed and followed through to a checked improvement. If none occurred, show monitoring and reporting readiness.",
  "safe-systems":
    "Show the referral, transfer or handover pathway, named responsibility and how missing information is followed up.",
  safeguarding:
    "Show how staff recognise and escalate concerns for the people supported. No recorded incidents does not remove the need for training and reporting arrangements.",
  "involving-people-risks":
    "Show an individual risk assessment agreed with the person, including their preferences, proportionate controls and review.",
  "safe-environments":
    "Identify the environments, equipment and technology you actually control. Homecare still needs individual home risk assessment and escalation of hazards.",
  "safe-staffing":
    "Show staffing matched to needs and workload, recruitment checks, role competencies, cover and supervision.",
  "infection-control":
    "Show precautions proportionate to the care delivered, relevant equipment cleaning, staff competence and action on audit findings.",
  "medicines-optimisation":
    "Separate prescribing, supply, storage, administration and prompting. Identify your responsibility and the responsibility of the person, pharmacy, prescriber or other provider.",
  "assessing-needs":
    "Show assessment and review of needs, communication and preferences, and how findings change the care delivered.",
  "evidence-based-care":
    "Choose an actual intervention or support activity and show how current guidance is reflected in practice and checked.",
  "team-working":
    "Show how your team communicates with other professionals and how responsibility for actions and results is closed off.",
  "healthy-lives":
    "Show how the service supports relevant health goals and independence within its role, with onward referral when needed.",
  "monitoring-outcomes":
    "Choose outcomes meaningful to this service and population; record baseline, trend, action and whether people benefited.",
  consent:
    "Show that people receive understandable information and can make choices. Include capacity and supported decision-making arrangements where relevant.",
  "kindness-dignity":
    "Show how privacy, dignity and respectful communication work in real encounters, including feedback and observed practice.",
  individuality:
    "Show adjustments to care for a person’s identity, communication, beliefs, needs and preferences.",
  "independence-choice":
    "Show how people exercise meaningful choice and control, including choices the service finds difficult to accommodate.",
  "immediate-needs":
    "Show how staff recognise and respond to distress, deterioration or urgent needs, including escalation and response times.",
  "workforce-wellbeing":
    "Show how staff workload, wellbeing, supervision and support are reviewed and how concerns lead to action.",
  "person-centred-care":
    "Use a care or treatment example demonstrating shared goals, individual preferences and review with the person.",
  continuity:
    "Show how care continues when staff, providers or settings change and how gaps are identified and resolved.",
  "providing-information":
    "Show accurate accessible information about care, costs, consent, contacts and complaints in formats people can use.",
  "listening-involving":
    "Show accessible feedback routes, involvement in decisions, an action taken and how the change was communicated back.",
  "equity-access":
    "Identify barriers in your population, record reasonable adjustments and show whether access improved.",
  "equity-outcomes":
    "Compare experiences and outcomes across relevant groups; explain gaps and action, recognising small sample sizes.",
  "planning-future":
    "Show future care preferences and relevant transitions. End-of-life evidence is needed only where that aspect is within the service’s role.",
  "shared-direction":
    "Show a service-specific purpose and priorities understood by staff and informed by people receiving care.",
  "capable-leaders":
    "Show named accountable leaders, relevant competence, visibility, supervision and action on quality concerns.",
  "freedom-speak-up":
    "Show confidential routes for concerns, staff awareness, protection from disadvantage and evidence that concerns are acted on.",
  "workforce-equality":
    "Show fair recruitment and progression, adjustments for staff and action on discrimination or inequality.",
  governance:
    "Show risks, audits, actions, named owners, deadlines and evidence that leaders check whether improvements worked locally.",
  partnerships:
    "Show the partners this service actually needs, shared goals, working arrangements and examples of resolved cross-service problems.",
  "learning-improvement":
    "Show an improvement cycle: issue, baseline, change, re-measurement and sharing of learning.",
  "environmental-sustainability":
    "Choose environmental impacts within this service’s control, proportionate actions and measures of progress.",
};
export function serviceGuidance(site, groupId, itemId, elementId) {
  const profile = blankProfile(site),
    group = groupsForSite(site, profile).find((g) => g.id === groupId),
    item = group?.items.find((i) => i.id === itemId),
    element = item?.elements.find((e) => e.id === elementId);
  const national = countryOf(site) !== "England";
  const base = national
    ? {
        groupLabel: group?.label || groupId,
        itemLabel: item?.label || itemId,
        purpose:
          item?.guidance ||
          "Review the source framework and this organisation’s responsibilities.",
        categoryLabel: element?.label || "Evidence and review",
        categoryHelp: item?.guidance || "",
        proofExamples: [],
        resources: group?.source
          ? [
              {
                label: `${group.regulator} — ${item?.reference || group.frameworkLabel}`,
                url: group.source,
              },
            ]
          : [],
      }
    : elementGuidance(groupId, itemId, elementId);
  const labels = profile.serviceTypes.map(
    (id) => catalogue.services.find((s) => s.id === id)?.label || id,
  );
  const context = profile.serviceTypes
    .map((id) => SERVICE_CONTEXT[id])
    .filter(Boolean);
  const functions = catalogue.functions
    .filter((f) => profile.functions[f.id] === "yes")
    .map((f) => f.label);
  const functionNotes = [];
  if (itemId === "medicines-optimisation" || itemId === "medicines") {
    for (const id of ["prescribing", "administration", "prompting"])
      functionNotes.push(
        `${catalogue.functions.find((f) => f.id === id).label}: ${profile.functions[id] || "unknown"}.`,
      );
    if (
      ["prescribing", "administration", "prompting"].every(
        (id) => profile.functions[id] === "no",
      )
    )
      functionNotes.push(
        "No medicines function is declared. Review the scope and record any remaining advice, referral or escalation responsibilities before excluding individual evidence items.",
      );
  }
  if (
    profile.functions.remote === "yes" &&
    [
      "consent",
      "equity-access",
      "providing-information",
      "safe-environments",
    ].includes(itemId)
  )
    functionNotes.push(
      "For remote care, include accessible digital journeys, identity/consent checks, communication failures and escalation to face-to-face care where relevant.",
    );
  if (
    profile.functions.children === "yes" &&
    ["consent", "safeguarding", "assessing-needs"].includes(itemId)
  )
    functionNotes.push(
      "Include age-appropriate communication, safeguarding and the relevant consent/parental responsibility arrangements for children.",
    );
  if (
    profile.functions.sedation === "yes" &&
    [
      "safe-staffing",
      "immediate-needs",
      "safe-environments",
      "consent",
    ].includes(itemId)
  )
    functionNotes.push(
      "Include sedation-specific competencies, equipment, consent, monitoring and emergency arrangements where relevant.",
    );
  const focus = national ? base.purpose : TOPICS[itemId] || base.purpose;
  const examples = {
    "peoples-experience": `Feedback from people using ${site?.name || "this service"} about ${base.itemLabel.toLowerCase()}, including adjustments for communication needs and what changed as a result.`,
    "staff-feedback": `A staff discussion, supervision record or survey about ${base.itemLabel.toLowerCase()}, with concerns, actions and follow-up.`,
    "partner-feedback": `Feedback from the actual referral, commissioning or care partners involved in ${base.itemLabel.toLowerCase()}, with actions agreed.`,
    observation: `A dated observation of ${base.itemLabel.toLowerCase()} in the actual care environment or digital journey, with findings and actions.`,
    processes: `An example record and audit showing how ${base.itemLabel.toLowerCase()} works in practice, rather than a policy alone.`,
    outcomes: `A proportionate measure of ${base.itemLabel.toLowerCase()} over time, with baseline, changes and the impact on people.`,
  };
  return {
    ...base,
    serviceLabel: labels.join(" + ") || "Service profile not confirmed",
    context,
    functionNotes,
    focus,
    purpose: [base.purpose, focus, ...context, ...functionNotes].join("\n\n"),
    proofExamples: [
      examples[elementId] ||
        `For ${site?.name || "this organisation"}, show the actual arrangements for ${base.itemLabel.toLowerCase()}, relevant records or observations, the person’s experience and a dated review with actions. ${item?.reference ? "Source reference: " + item.reference + "." : ""}`,
      ...(profile.stage === "pre-opening"
        ? [
            "Provide readiness evidence now; identify the operational outcomes to collect after opening.",
          ]
        : []),
    ],
    activeFunctions: functions,
  };
}
